import * as React from 'react'
import { connect } from 'react-redux'
import { matchPath } from 'react-router'
import { Switch, Route, Redirect } from 'react-router-dom'

import { QACheck } from '../lib/interfaces/QACheck.interface'
import { Accident } from '../lib/interfaces/Accident.interface'
import { Employee } from '../lib/interfaces/Employee.interface'
import { User, hasPermission } from '../lib/interfaces/User.interface'

import { authLogout } from '../store/auth/actions'

import Nav from './Nav.component'
import Sidebar from './Sidebar.component'
import DashboardPage from './dashboard/Dashboard.page'

// Documentation
import DocumentationPage from './docs/Documentation.page'

// Applications
import JobPositionsPage from './applications/JobPositions.page'
import ApplicationDetailsPage from './applications/ApplicationDetails.page'
import ApplicationsDashboardPage from './applications/ApplicationsDashboard.page'

// Accidents
import CurrentAccident from './accidents/components/CurrentAccident.component'
import AccidentListPage from './accidents/AccidentList.page'
import AccidentReportsPage from './accidents/AccidentReports.page'
import AccidentDetailsPage from './accidents/AccidentDetails.page'
import AccidentCreationPage from './accidents/AccidentCreation.page'
import ClaimCreationPage from './accidents/ClaimCreation.page'

// Employees
import EmployeeListPage from './employees/EmployeeList.page'
import EmployeeDetailsPage from './employees/EmployeeDetails.page'
import CurrentEmployee from './employees/components/CurrentEmployee.component'

// Invoices
import InvoicesListPage from './invoices/InvoicesList.page'
import InvoiceDetailsPage from './invoices/InvoiceDetails.page'
import InvoiceCreationPage from './invoices/InvoiceCreation.page'

// Terminations
import TerminationListPage from './terminations/TerminationsList.page'
import TerminationDetailsPage from './terminations/TerminationDetails.page'
import TerminationEditionPage from './terminations/TerminationEdition.page'

// Admin
import UsersManagementPage from './admin/UsersManagement.page'

// Scans
import ScanQRLoginPage from './scans/ScanQRLogin.page'
import ScanListPage from './scans/ScanList.page'
import ScanByEmployeeListPage from './scans/ScanByEmployeeList.page'

// QA
import QAChecksListPage from './qa-checks/QAChecksList.page'
import CurrentQACheck from './qa-checks/components/CurrentQACheck.component'

import { fetchLocationsDispatchCommand, toggleSidebarActionDispatchCommand } from 'src/store/app/actions'
import { RootState } from 'src/store/rootReducer'

type DashboardState = {
  sidebarOpen: boolean
}

type DashboardProps = {
  user: User,
  onLogout: () => void,
  sidebarOpen: boolean,
  currentQACheck: QACheck,
  currentEmployee: Employee,
  currentAccident: Accident,
  onRequestLocations: () => void,
  toggleSidebar: (sidebarOpen: boolean) => void
}

class DashboardRoutes extends React.Component<DashboardProps, DashboardState> {

  state: DashboardState = {
    sidebarOpen: this.props.sidebarOpen
  }

  componentDidMount() {
    // Fetch locations
    this.props.onRequestLocations()
  }

  render() {
    const {
      user,
      onLogout,
      sidebarOpen,
      currentQACheck,
      currentAccident,
      currentEmployee
    } = this.props

    const sidebarStatus = sidebarOpen ? 'open' : 'collapsed'

    const employeesMatch = matchPath(location.pathname, { path: '/employees', exact: false })
    const terminationsMatch = matchPath(location.pathname, { path: '/terminations', exact: false })
    const accidentsMatch = matchPath(location.pathname, { path: '/accidents', exact: false })
    const applicationsMatch = matchPath(location.pathname, { path: '/applications', exact: false })

    const showUserSearch = !!employeesMatch || !!terminationsMatch
    const showAccidentSearch = !!accidentsMatch
    const showApplicationSearch = !!applicationsMatch

    return (
      <div className="ggdashboard d-flex">
        <div className="d-flex flex-row flex-grow-1">
          <Sidebar status={sidebarStatus} user={user} />

          <div className="d-flex flex-grow-1 flex-column">
            <Nav
              user={user}
              onLogout={onLogout}
              userSearch={showUserSearch}
              sidebarStatus={sidebarStatus}
              accidentSearch={showAccidentSearch}
              applicationSearch={showApplicationSearch}
              onToggleSidebar={() => this.props.toggleSidebar(!this.props.sidebarOpen)}
            />

            <div className="flex-grow-1 min-height-0 overflow-y-scroll">
              <Switch>
                <Route path="/documentation" component={DocumentationPage} />

                {/* Dashboard */}
                <Route exact path="/dashboard" component={DashboardPage} />

                {/* Employees */}
                <Route exact path="/employees" component={EmployeeListPage} />
                <Route exact path="/employees/search/:search?/:location_id?" component={EmployeeListPage} />
                <Route exact path="/employees/:employeeId" component={EmployeeDetailsPage} />

                {/* Terminations */}
                <Route exact path="/terminations" component={TerminationListPage} />
                <Route path="/terminations/:terminationId/edit" component={TerminationEditionPage} />
                <Route path="/terminations/:terminationId" component={TerminationDetailsPage} />

                {/* Applications */}
                <Route exact path="/applications/positions" component={JobPositionsPage} />
                <Route exact path="/applications" component={ApplicationsDashboardPage} />
                <Route exact path="/applications/:applicationId" component={ApplicationDetailsPage} />

                {/* Accident reporting */}
                <Route exact path="/accidents/report" component={AccidentCreationPage} />
                <Route exact path="/accidents/:accidentId/edit" component={AccidentCreationPage} />
                <Route exact path="/accidents/:accidentId/claims/add" component={ClaimCreationPage} />
                <Route exact path="/accidents/:accidentId/claims/:claimId" component={ClaimCreationPage} />

                {/* Accidents */}
                <Route exact path="/accidents" component={AccidentListPage} />
                <Route path="/accidents/reports" component={AccidentReportsPage} />
                <Route path="/accidents/:accidentId" component={AccidentDetailsPage} />

                {/* Invoices */}
                <Route exact path="/invoices" component={InvoicesListPage} />
                <Route exact path="/invoices/add" component={InvoiceCreationPage} />
                <Route path="/invoices/:invoiceId" component={InvoiceDetailsPage} />

                {/* Scan */}
                <Route exact path="/scan/employee" component={ScanByEmployeeListPage} />
                <Route exact path="/scan" component={ScanListPage} />
                <Route path="/scan/login" component={ScanQRLoginPage} />

                {/* QA */}
                <Route exact path="/qa-check" component={QAChecksListPage} />

                {/* Admin */}
                <Route exact path="/admin/users" component={UsersManagementPage} />

                <Redirect to="/dashboard" />
              </Switch>
            </div>

            {currentQACheck &&
              <CurrentQACheck />
            }
            {currentEmployee &&
              <CurrentEmployee />
            }
            {currentAccident &&
              <CurrentAccident />
            }
          </div>
        </div>
      </div>
    )
  }
}

const mapStateToProps = ({ auth, accidents, qaChecks, employees, app }: RootState) => ({
  user: auth.user,
  sidebarOpen: app.sidebarOpen,
  currentQACheck: qaChecks.currentQACheck,
  currentAccident: accidents.currentAccident,
  currentEmployee: employees.currentEmployee
})

const mapDispatchToProps = (dispatch) => ({
  onLogout: () => dispatch(authLogout()),
  onRequestLocations: () => dispatch(fetchLocationsDispatchCommand()),
  toggleSidebar: (sidebarOpen: boolean) => dispatch(toggleSidebarActionDispatchCommand(sidebarOpen))
})

export default connect(mapStateToProps, mapDispatchToProps)(DashboardRoutes)



// WEBPACK FOOTER //
// ./src/routes/index.tsx